import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../../../lib/firebase';
import { mapFirebaseAuthError } from '../firebase-errors';
import { Logo } from '../../../app/components/Logo';
import { Card } from '../../../components/ui/Card';
import { Label } from '../../../components/ui/Label';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';

interface AuthStepProps {
  mode: 'signup' | 'login';
}

/**
 * Tela 1 — Signup/login (/signup, /login). Firebase Auth (e-mail+senha e
 * Google). On success, `AuthContext`'s `onAuthStateChanged` picks up the
 * new session and `RedirectIfAuthenticated` (guards.tsx), wrapping this
 * route, sends the user to `/onboarding/cnpj` (no org yet) — no manual
 * `navigate()` call needed here.
 */
export function AuthStep({ mode }: AuthStepProps) {
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState<'form' | 'google' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isSignup = mode === 'signup';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting('form');
    try {
      if (isSignup) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      setError(mapFirebaseAuthError(err));
      setSubmitting(null);
    }
  }

  async function handleGoogle() {
    setError(null);
    setSubmitting('google');
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      setError(mapFirebaseAuthError(err));
      setSubmitting(null);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background font-sans text-foreground">
      <header className="border-b border-border px-4 py-4 sm:px-6">
        <Logo />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6">
        <Card className="flex w-full max-w-md flex-col gap-6 p-8">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-medium text-foreground sm:text-3xl">
              {isSignup ? 'Criar conta' : 'Entrar'}
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {isSignup
                ? 'Crie sua conta para começar a cotar e comparar frete entre transportadoras.'
                : 'Entre com sua conta Trivio.'}
            </p>
          </div>

          {error ? (
            <Alert variant="error" title="Não foi possível continuar">
              <span id={errorId}>{error}</span>
            </Alert>
          ) : null}

          <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-2">
              <Label htmlFor={emailId}>E-mail</Label>
              <Input
                id={emailId}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-describedby={error ? errorId : undefined}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={passwordId}>Senha</Label>
              <Input
                id={passwordId}
                type="password"
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                minLength={6}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-describedby={error ? errorId : undefined}
              />
            </div>

            <Button type="submit" variant="default" disabled={submitting !== null} className="mt-2">
              {submitting === 'form' ? 'Enviando…' : isSignup ? 'Criar conta' : 'Entrar'}
            </Button>
          </form>

          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-border" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">ou</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogle}
            disabled={submitting !== null}
          >
            {submitting === 'google' ? 'Conectando…' : 'Continuar com Google'}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            {isSignup ? (
              <>
                Já tem conta?{' '}
                <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
                  Entrar
                </Link>
              </>
            ) : (
              <>
                Ainda não tem conta?{' '}
                <Link to="/signup" className="font-medium text-primary underline-offset-4 hover:underline">
                  Criar conta
                </Link>
              </>
            )}
          </p>
        </Card>
      </main>
    </div>
  );
}
